import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Eye, Pencil, Send, Download, BellRing, MessageCircle, Loader2, Wallet } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UNPAID_STATUSES = ["draft", "sent", "partially_paid"];

export const isInvoiceOverdue = (invoice) => {
  if (!invoice.due_date || !UNPAID_STATUSES.includes(invoice.status)) return false;
  return invoice.due_date < new Date().toISOString().split("T")[0];
};

export const daysOverdue = (invoice) =>
  Math.floor((new Date() - new Date(invoice.due_date)) / 86400000);

const formatINR = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount || 0);

export const InvoiceRowActions = ({ invoice, canManage, onPreview, onEdit, onStatusChange, onRecordPayment, onRefresh }) => {
  const [busy, setBusy] = useState("");
  const unpaid = UNPAID_STATUSES.includes(invoice.status);
  const overdue = isInvoiceOverdue(invoice);

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await axios.get(`${API_URL}/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setBusy("");
    }
  };

  const sendReminder = async () => {
    setBusy("reminder");
    try {
      const res = await axios.post(`${API_URL}/invoices/${invoice.id}/send-reminder`);
      toast.success(res.data.message);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setBusy("");
    }
  };

  const shareWhatsApp = async () => {
    setBusy("whatsapp");
    try {
      const { data } = await axios.post(`${API_URL}/invoices/${invoice.id}/share-link`);
      const link = `${process.env.REACT_APP_BACKEND_URL}/api/public/invoices/${data.share_token}`;
      const text = `Dear ${data.client_name},\n\nPlease find your invoice ${data.invoice_number} for ${formatINR(data.total_amount)} (due ${data.due_date}).\nView / download PDF: ${link}\n\nRegards,\nArdhanageshwar Logistics Pvt. Ltd.`;
      const phone = (data.client_phone || "").replace(/\D/g, "");
      const digits = phone.length === 10 ? `91${phone}` : phone;
      window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      if (!digits) toast.info("Client has no phone on file — pick a contact in WhatsApp");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create share link");
    } finally {
      setBusy("");
    }
  };

  const spin = (key, Icon) => busy === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />;

  return (
    <div className="flex justify-end gap-1">
      <Button variant="ghost" size="icon" onClick={() => onPreview(invoice)} data-testid={`preview-invoice-${invoice.id}`} title="Preview">
        <Eye className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="icon" onClick={downloadPdf} disabled={!!busy} data-testid={`download-invoice-${invoice.id}`} title="Download PDF">
        {spin("pdf", Download)}
      </Button>
      {canManage && (
        <Button variant="ghost" size="icon" onClick={shareWhatsApp} disabled={!!busy} data-testid={`whatsapp-invoice-${invoice.id}`} title="Share on WhatsApp" className="text-green-400">
          {spin("whatsapp", MessageCircle)}
        </Button>
      )}
      {canManage && unpaid && (
        <Button variant="ghost" size="icon" onClick={sendReminder} disabled={!!busy} data-testid={`remind-invoice-${invoice.id}`}
          title={invoice.last_reminder_at ? `Send reminder (last sent ${invoice.last_reminder_at.split("T")[0]})` : "Email payment reminder"}
          className={overdue ? "text-red-400" : "text-amber-400"}>
          {spin("reminder", BellRing)}
        </Button>
      )}
      {canManage && invoice.status === "draft" && (
        <>
          <Button variant="ghost" size="icon" onClick={() => onEdit(invoice)} data-testid={`edit-invoice-${invoice.id}`} title="Edit charges & GST">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onStatusChange(invoice, "sent")} data-testid={`send-invoice-${invoice.id}`} title="Mark as Sent" className="text-blue-400">
            <Send className="w-4 h-4" />
          </Button>
        </>
      )}
      {canManage && unpaid && (
        <Button variant="ghost" size="icon" onClick={() => onRecordPayment(invoice)} data-testid={`pay-invoice-${invoice.id}`} title="Record Payment" className="text-emerald-400">
          <Wallet className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};
